import { authClient } from "@crm/access/auth/client";
import type { User } from "@crm/access/authorization/client";
import { useAtomRefresh, useAtomSuspense } from "@effect/atom-react";
import { NavigationBar } from "@crm/ui/components/class-progress/navigation-bar";
import { TaskCompletionPage } from "@crm/ui/components/class-progress/task-completion-page";
import { TaskTypeTabs } from "@crm/ui/components/class-progress/task-type-tabs";
import { SearchableSelect } from "@crm/ui/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@crm/ui/components/ui/select";
import type { TaskType } from "@crm/ui/hooks/use-group-active-task";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useEffect, useMemo, useState } from "react";
import { studentTaskContextAtom } from "#app/student-tasks/queries";
import { signOutAndLeaveWorkbench } from "../../../lib/account-session";

export const Route = createFileRoute("/_protected/student-tasks/")({
  head: () => ({}),
  component: StudentTasksPage,
});

const TAB_ORDER: TaskType[] = ["in_class", "evening", "mikequan"];

type BoundStudentUser = User & {
  role: "student";
  studentId: string;
};

function signOutAndReload() {
  return signOutAndLeaveWorkbench();
}

function StudentTasksPage() {
  // workbench 无 CRM 的 ClientAuthContext provider，与 _protected/route.tsx 一致直取 session
  const { data: session, isPending } = authClient.useSession();
  const user = session?.user as User | undefined;

  if (isPending) {
    return null;
  }

  if (user?.role !== "student") {
    return null;
  }

  if (!user.studentId) {
    return <UnlinkedStudentAccount user={user} />;
  }

  return <StudentTasksContent user={user as BoundStudentUser} />;
}

function StudentTasksContent({ user }: { user: BoundStudentUser }) {
  // Fetch student's task context（effect-atom 版，替代 useSuspenseQuery）。
  // - useAtomSuspense：首次加载 Suspense（挂起直到原子离开 Initial）。
  // - useAtomRefresh + setInterval：queryAtom 无 refetchInterval 轮询参数，
  //   用 `setInterval(refresh, 30_000)` 实现等效 30s 轮询；
  //   swr 原子在刷新期间保留旧数据（suspendOnWaiting=false），轮询不会闪屏。
  const taskContextAtom = studentTaskContextAtom([user.studentId]);
  const result = useAtomSuspense(taskContextAtom);
  const refreshTaskContext = useAtomRefresh(taskContextAtom);

  useEffect(() => {
    const timer = setInterval(refreshTaskContext, 30_000);
    return () => clearInterval(timer);
  }, [refreshTaskContext]);

  const classSessions = result.value?.classSessions || [];

  // State for selected class session (default to first one)
  const [selectedSessionId, setSelectedSessionId] = useState(
    classSessions[0]?.classSessionId || "",
  );

  // Auto-sync selectedSessionId when classSessions change (e.g. after polling)
  useEffect(() => {
    if (classSessions.length === 0) {
      return;
    }
    const exists = classSessions.some((cs) => cs.classSessionId === selectedSessionId);
    if (!exists) {
      setSelectedSessionId(classSessions[0].classSessionId);
    }
  }, [classSessions, selectedSessionId]);

  // Find the selected class session data
  const selectedSession = classSessions.find((cs) => cs.classSessionId === selectedSessionId);

  const availableTasks = selectedSession?.tasks || [];

  // Group tasks by type
  type AvailableTask = (typeof availableTasks)[number];
  const tasksByType = useMemo(() => {
    const map: Record<TaskType, AvailableTask[]> = {
      in_class: [],
      evening: [],
      mikequan: [],
    };
    for (const task of availableTasks) {
      if (task.type in map) {
        map[task.type as TaskType].push(task);
      }
    }
    return map;
  }, [availableTasks]);

  // Set of types that have active tasks (green dot indicator)
  const activeTaskTypes = useMemo(() => {
    const set = new Set<TaskType>();
    for (const task of availableTasks) {
      if (task.status === "active") {
        set.add(task.type as TaskType);
      }
    }
    return set;
  }, [availableTasks]);

  // Tab state — default to first type with an active task
  const [activeTab, setActiveTab] = useState<TaskType>(
    () =>
      TAB_ORDER.find((t) => activeTaskTypes.has(t)) ??
      TAB_ORDER.find((t) => tasksByType[t].length > 0) ??
      "in_class",
  );

  // Only move off an empty tab; don't yank the user back on poll refreshes.
  useEffect(() => {
    if (tasksByType[activeTab].length > 0) {
      return;
    }
    const newTab =
      TAB_ORDER.find((t) => activeTaskTypes.has(t)) ??
      TAB_ORDER.find((t) => tasksByType[t].length > 0) ??
      "in_class";
    setActiveTab(newTab);
  }, [activeTab, activeTaskTypes, tasksByType]);

  // Tasks for current tab
  const currentTypeTasks = tasksByType[activeTab];

  // Selected task within current type (for when there are multiple e.g. active + archived)
  const [selectedTaskId, setSelectedTaskId] = useState("");

  // Sync selected task when tab or tasks change — prefer active task
  useEffect(() => {
    const tasks = tasksByType[activeTab];
    const activeTask = tasks.find((t) => t.status === "active");
    setSelectedTaskId(activeTask?.id ?? tasks[0]?.id ?? "");
  }, [activeTab, tasksByType]);

  const currentTask =
    currentTypeTasks.find((t) => t.id === selectedTaskId) ?? currentTypeTasks[0] ?? null;

  // Case 1: No active class sessions
  if (classSessions.length === 0) {
    return (
      <div className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="rounded-md border border-border bg-white p-8 shadow-s">
            <div className="mb-6 text-center">
              <div className="mb-4 text-6xl">📚</div>
              <h1 className="font-bold text-2xl text-ink">暂无进行中的课程</h1>
            </div>
            <div className="text-center text-ink/70">
              <p className="leading-relaxed">您当前没有正在进行的课程。</p>
              <p className="mt-2 leading-relaxed">请等待新课程开始或联系管理员。</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 3: No group assigned for selected session — 解释性引导 + 只读作业列表
  // （首页待办按班级全量展示；未分组时无法提交，此处承接为只读视图，避免"首页有作业却交不了"）
  if (!selectedSession?.groupId) {
    const TASK_TYPE_LABELS: Record<string, string> = {
      in_class: "课中任务",
      evening: "晚课任务",
      mikequan: "麦圈任务",
    };
    const formatDate = (ms: number | null) =>
      ms
        ? new Intl.DateTimeFormat("zh-CN", {
            month: "long",
            day: "numeric",
          }).format(ms)
        : "—";

    return (
      <div className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          {/* Class session selector if multiple */}
          {classSessions.length > 1 && (
            <div className="mb-4 rounded-md border border-border bg-white p-4">
              <label className="mb-2 block font-medium text-ink text-sm">当前班期:</label>
              <SearchableSelect
                onChange={setSelectedSessionId}
                options={classSessions.map((cs) => ({
                  id: cs.classSessionId,
                  name: `${cs.classSessionName}${cs.enrollmentStatus === "finished" ? " (已结课)" : ""}`,
                }))}
                placeholder="搜索班期..."
                value={selectedSessionId}
              />
            </div>
          )}

          <div className="rounded-md border border-warning bg-white p-8 shadow-s">
            <div className="mb-6 text-center">
              <div className="mb-4 text-6xl">👥</div>
              <h1 className="font-bold text-2xl text-ink">您尚未分配到小组</h1>
            </div>
            <div className="text-center text-ink/70">
              <p className="leading-relaxed">
                在 <strong>{selectedSession?.classSessionName}</strong>{" "}
                中，您还没有被分配到学习小组。
              </p>
              <p className="mt-2 leading-relaxed">
                请联系教练分组后再提交作业。以下是当前班期的作业安排（只读）：
              </p>
            </div>
          </div>

          {/* 只读作业列表（未分组：可查看安排，不可提交） */}
          {availableTasks.length > 0 && (
            <div className="mt-4 space-y-3">
              {availableTasks.map((task) => (
                <div
                  className="rounded-md border border-border bg-white p-4 shadow-s"
                  key={task.id}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-ink">{task.title}</div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-ink/60 text-xs">
                      {TASK_TYPE_LABELS[task.type] ?? task.type}
                    </span>
                  </div>
                  {task.description && (
                    <p className="mt-2 text-ink/70 text-sm">{task.description}</p>
                  )}
                  <p className="mt-2 text-ink/50 text-xs">
                    截止日期: {formatDate(task.deadline)}
                    {task.status === "archived" && " · 已结束"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Case 4: No tasks at all for selected session
  if (availableTasks.length === 0) {
    return (
      <div className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          {/* Class session selector if multiple */}
          {classSessions.length > 1 && (
            <div className="mb-4 rounded-md border border-border bg-white p-4">
              <label className="mb-2 block font-medium text-ink text-sm">当前班期:</label>
              <SearchableSelect
                onChange={setSelectedSessionId}
                options={classSessions.map((cs) => ({
                  id: cs.classSessionId,
                  name: `${cs.classSessionName}${cs.enrollmentStatus === "finished" ? " (已结课)" : ""}`,
                }))}
                placeholder="搜索班期..."
                value={selectedSessionId}
              />
            </div>
          )}

          <div className="rounded-md border border-border bg-white p-8 shadow-s">
            <div className="mb-6 text-center">
              <div className="mb-4 text-6xl">📝</div>
              <h1 className="font-bold text-2xl text-ink">{selectedSession.groupName}</h1>
              <p className="mt-2 text-ink/60 text-lg">暂无作业</p>
            </div>
            <div className="text-center text-ink/70">
              <p className="leading-relaxed">该班期还没有发布作业。</p>
              <p className="mt-2 leading-relaxed">请等待老师发布新作业。</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 5: Has tasks — NavigationBar + Tabs + TaskCompletionPage
  return (
    <div className="px-2 py-6 font-sans text-ink">
      <div className="mx-auto max-w-2xl space-y-4 px-6">
        {/* 班期选择（仅多班期时显示） */}
        {classSessions.length > 1 && (
          <div className="rounded-md border border-border bg-white p-4">
            <label className="mb-2 block font-medium text-ink text-sm">当前班期:</label>
            <Select onValueChange={setSelectedSessionId} value={selectedSessionId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {classSessions.map((cs) => (
                  <SelectItem key={cs.classSessionId} value={cs.classSessionId}>
                    {cs.classSessionName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 顶部导航栏 */}
        <NavigationBar
          groupName={selectedSession.groupName ?? ""}
          onChangeStudent={signOutAndReload}
          selectedStudent={{
            id: user.studentId,
            name: user.name,
          }}
        />

        {/* Tab 切换 */}
        <TaskTypeTabs
          activeTab={activeTab}
          activeTasks={activeTaskTypes}
          onTabChange={setActiveTab}
        />

        {/* 同类型多任务时显示选择器（如有活跃+已归档） */}
        {currentTypeTasks.length > 1 && (
          <Select onValueChange={setSelectedTaskId} value={selectedTaskId}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currentTypeTasks.map((task) => (
                <SelectItem key={task.id} value={task.id}>
                  {task.title}
                  {task.status === "active" && " (进行中)"}
                  {task.status === "archived" && " (已结束)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* 任务内容区 */}
        <Suspense
          fallback={
            <div className="flex min-h-[200px] items-center justify-center">
              <p className="text-ink/60">加载中...</p>
            </div>
          }
        >
          {currentTask ? (
            <TaskCompletionPage
              classSessionId={selectedSession.classSessionId}
              groupId={selectedSession.groupId}
              key={currentTask.id}
              readOnly={!currentTask.canSubmit}
              selectedStudent={{
                id: user.studentId,
                name: user.name,
              }}
              task={currentTask}
            />
          ) : (
            <div className="rounded-md border border-border bg-white p-8">
              <div className="py-12 text-center">
                <div className="mb-4 text-6xl">📝</div>
                <p className="mb-4 text-gray-600 text-xl">暂无活跃任务</p>
                <p className="text-gray-500 text-sm">请等待老师发布新任务</p>
              </div>
            </div>
          )}
        </Suspense>
      </div>
    </div>
  );
}

function UnlinkedStudentAccount({ user }: { user: User }) {
  return (
    <div className="flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="rounded-md border border-warning bg-white p-8 shadow-s">
          <div className="mb-6 text-center">
            <div className="mb-4 text-6xl">⚠️</div>
            <h1 className="font-bold text-2xl text-ink">账号未关联学员档案</h1>
          </div>
          <div className="space-y-4 text-ink/70">
            <p className="leading-relaxed">
              您的账号 <strong>{user.name || "未知"}</strong> 尚未关联到学员档案。
            </p>
            <p className="leading-relaxed">请联系管理员将您添加到学员系统中。</p>
          </div>
          <div className="mt-6">
            <button
              className="w-full rounded-md border border-border bg-white px-6 py-3 font-medium text-ink text-lg transition-all hover:bg-muted"
              onClick={signOutAndReload}
              type="button"
            >
              登出
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
